import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { instrumentTypeOptions } from "./filter";

const FormSchema = z.object({
  instrument_type: z.string().min(1, "Instrument Type is required"),
  instrument_id: z.coerce
    .number({ message: "Instrument ID is required" })
    .min(1, "Instrument ID must be greater than zero"),
  diff: z.string().nullable().optional(),
});

export function StrategyChildrenEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                StrategyChildren {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: instrument_type & instrument_id */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="instrument_type"
                    label={t(`${table_name}.columns.instrument_type`, {
                      ns: "tables",
                      defaultValue: "Instrument Type",
                    })}
                    options={instrumentTypeOptions}
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    name="instrument_id"
                    label={t(`${table_name}.columns.instrument_id`, {
                      ns: "tables",
                      defaultValue: "Instrument ID",
                    })}
                    type="number"
                    control={form.control}
                    isRequired
                    allowZero={false}
                    allowNegative={false}
                  />
                </div>

                {/* Row 2: diff JSON */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="diff"
                    label={t(`${table_name}.columns.diff`, {
                      ns: "tables",
                      defaultValue: "Diff",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.json_diff")}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["instrument_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
